import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { RotateCcwIcon } from 'lucide-react';
import { PresentationBar, type View } from './components/PresentationBar';
import { App as PoteligeoSnak } from './components/poteligeo-snak/App';
import { ConceptModal } from './components/concept/ConceptModal';
import { IntroStatement } from './components/IntroStatement';
import { InsightsView } from './components/insights/InsightsView';
import { PhoneViewport } from './components/PhoneViewport';
import { PhoneCue, PhoneCueInline } from './components/PhoneCue';
import { SnakInvitation } from './components/SnakInvitation';
import { useSnakChapter } from './hooks/useSnakChapter';
const LAST_CHAPTER = 7;
type AppProps = {
  /** Open the concept window on load. */
  conceptOpenOnLoad?: boolean;
};
export function App({
  conceptOpenOnLoad = false
}: AppProps) {
  const [view, setView] = useState<View>('main');
  const [conceptOpen, setConceptOpen] = useState(conceptOpenOnLoad);
  // Remounting the Snak restarts it at chapter 1 without touching its source.
  const [run, setRun] = useState(0);
  // Once the visitor touches the phone, the "try it" cue settles.
  const [engaged, setEngaged] = useState(false);
  const phoneRef = useRef<HTMLDivElement>(null);
  const chapter = useSnakChapter(phoneRef);
  // The meeting invitation appears once the visitor has had a moment on the final chapter.
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    if (chapter !== LAST_CHAPTER) return;
    const t = window.setTimeout(() => setFinished(true), 2500);
    return () => window.clearTimeout(t);
  }, [chapter]);
  const openConcept = useCallback(() => setConceptOpen(true), []);
  const closeConcept = useCallback(() => setConceptOpen(false), []);
  const openInsights = useCallback(() => {
    setConceptOpen(false);
    setView('insights');
  }, []);
  const goHome = useCallback(() => setView('main'), []);
  // Lift the cover and start the Snak's own guided playback (via its ▶ button).
  const startPreview = useCallback(() => {
    setEngaged(true);
    window.setTimeout(() => {
      const play = phoneRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Start guided story"]');
      play?.click();
    }, 200);
  }, []);
  return <div className="flex min-h-screen w-full flex-col bg-paper" data-id="element-0" data-mp="86w3alkjrvxo">
      <PresentationBar view={view} onOpenInsights={openInsights} onHome={goHome} data-id="element-1" data-mp="3gkv1zuywlvev" />
      <main className="relative flex flex-1 flex-col items-center md:justify-center md:py-8 lg:flex-row lg:items-center lg:gap-16 lg:px-10 xl:gap-24" data-id="element-2" data-mp="3bmjrdi0sbgvl">
        <div className="order-2 w-full px-6 py-12 md:max-w-[500px] md:px-0 lg:order-1 lg:py-0" data-id="element-3" data-mp="3qcr0h74wjnjm">
          <IntroStatement onOpenConcept={openConcept} data-id="element-4" data-mp="3liwjwcklfoor" />
        </div>
        {/* Right padding reserves room for the moving side note so it is never pushed off-screen. */}
        <div className="order-1 flex w-full flex-1 flex-col items-center md:w-auto md:flex-none lg:order-2 lg:pr-[200px]" data-id="element-5" data-mp="2swlix0t2rlrf">
          <SnakInvitation finished={finished} onExplore={startPreview} data-id="element-6" data-mp="18czoyofpvnp4" />
          <div ref={phoneRef} className="relative flex w-full flex-1 flex-col md:w-auto md:flex-none" onPointerDown={() => setEngaged(true)} data-id="element-7" data-mp="1d4reqwr1tpe7">
            {/* Real handset proportions (~9:19.5): width follows the available height. */}
            <div className={`relative flex min-h-[640px] w-full flex-1 flex-col overflow-hidden bg-white transition-[box-shadow] duration-300 md:aspect-[9/19.5] md:h-[min(760px,calc(100vh-290px))] md:min-h-[580px] md:w-auto md:flex-none md:rounded-[44px] md:border-[10px] md:border-ink md:shadow-[0_40px_80px_-30px_rgba(14,27,51,0.45)] ${engaged ? '' : 'md:ring-[6px] md:ring-teal/15'}`} data-id="element-8" data-mp="13f7y7xqt67mi">
              <PhoneViewport data-id="element-9" data-mp="6ontuoo9heuc">
                <PoteligeoSnak key={run} data-id="element-10" data-mp="6nmew944k87c" />
              </PhoneViewport>
            </div>
            <PhoneCue engaged={engaged} hostRef={phoneRef} data-id="element-11" data-mp="ar29kzwmvyhe" />
          </div>
          <div className="flex items-center justify-center gap-3 px-6 py-3 md:mt-1 md:px-0 md:pb-0" data-id="element-12" data-mp="qwvrwf96wp6w">
            <PhoneCueInline data-id="element-13" data-mp="m5x6x2d9b2xb" />
            <span className="h-3 w-px bg-ink/15 lg:hidden" aria-hidden="true" data-id="element-14" data-mp="m5nfrbgh6pq1" />
            <button type="button" onClick={() => {
            setRun(r => r + 1);
            setFinished(false);
          }} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-[12px] font-medium text-ink transition-[border-color,transform] duration-150 ease-out hover:border-ink active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy" data-id="element-15" data-mp="h6km0n5y45xd">
              <RotateCcwIcon size={12} strokeWidth={2.25} aria-hidden="true" data-id="element-16" data-mp="vvp2nzx3e23d" />
              Restart
            </button>
          </div>
        </div>
        <AnimatePresence data-id="element-17" data-mp="3gmpiy63rqjk0">{view === 'insights' && <InsightsView onClose={goHome} data-id="element-18" data-mp="3globeblyof71" />}</AnimatePresence>
      </main>
      <ConceptModal open={conceptOpen} onClose={closeConcept} data-id="element-19" data-mp="1w4yivxjntnxx" />
    </div>;
}